import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

/**
 * Button variant types
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/**
 * Button size types
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Button component props
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size variant */
  size?: ButtonSize;
  /** Full width button */
  fullWidth?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Text shown next to the spinner while loading (defaults to "Loading...") */
  loadingText?: string;
}

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

const BASE_CLASSES = cn(
  'inline-flex items-center justify-center gap-2',
  'font-body font-semibold',
  'border rounded-md',
  'transition-colors duration-fast ease-default',
  'cursor-pointer',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:pointer-events-none'
);

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-gradient-to-b from-accent-primary to-accent-primary-deep border-accent-primary text-text-on-accent shadow-default',
    'hover:from-accent-primary-bright hover:to-accent-primary hover:shadow-md',
    'focus-visible:ring-accent-primary',
    // Disabled reads as a flat neutral button, never as a faded (and so washed-out) enabled one
    'disabled:bg-none disabled:bg-bg-tertiary disabled:border-border-default disabled:text-text-secondary disabled:shadow-none'
  ),
  secondary: cn(
    'bg-bg-tertiary border-border-default text-text-primary',
    'hover:bg-bg-hover hover:border-accent-primary',
    'focus-visible:ring-accent-primary',
    'disabled:opacity-50'
  ),
  ghost: cn(
    'bg-transparent border-transparent text-text-secondary',
    'hover:bg-bg-hover hover:text-text-primary',
    'focus-visible:ring-accent-primary',
    'disabled:opacity-50'
  ),
  danger: cn(
    'bg-semantic-error border-semantic-error text-text-inverse',
    'hover:opacity-90',
    'focus-visible:ring-semantic-error',
    'disabled:opacity-50'
  ),
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

/** Class string shared by Button and ButtonLink (44px minimum touch target). */
export function buttonClasses({ variant = 'primary', size = 'md', className }: ButtonClassOptions = {}): string {
  return cn(BASE_CLASSES, 'min-h-[44px]', VARIANT_CLASSES[variant], SIZE_CLASSES[size], className);
}

/**
 * Button - Base button component with variants and proper accessibility
 *
 * Features:
 * - Multiple variants (primary, secondary, ghost, danger)
 * - Three sizes (sm, md, lg)
 * - 44px minimum touch target for accessibility
 * - Loading state support
 * - Uses theme tokens for all styling
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  loadingText = 'Loading...',
  disabled,
  className,
  children,
  ...props
}, ref) {
  const widthStyles = fullWidth ? 'w-full' : '';

  return (
    <button
      ref={ref}
      className={buttonClasses({ variant, size, className: cn(widthStyles, className) })}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <span>{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
});
